import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAnimatedMetric } from '../../hooks/useAnimatedMetric';
import { useLocale } from '../../hooks/useLocale';
import { useRevealOnScroll } from '../../hooks/useRevealOnScroll';
import {
  getReadinessStatus,
  getTodayMetricValue,
} from './calendarHelpers';

interface Props {
  today: Record<string, unknown>;
  /** Tighter spectrum for scrollable day rows */
  compact?: boolean;
  /** Single-row chip + bar — calendar day list */
  inline?: boolean;
}

function FatigueIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2" y="7" width="16" height="10" rx="2" />
      <line x1="22" y1="11" x2="22" y2="13" />
    </svg>
  );
}

function ReadinessIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

function RecoveryIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 2s-7 4-7 10a7 7 0 0 0 14 0c0-6-7-10-7-10z" />
    </svg>
  );
}

/**
 * Calendar day readiness — VO2 Cloud Design Readiness.html variant 3 (segmented spectrum).
 */
export function CalendarReadinessWidget({ today, compact = false, inline = false }: Props) {
  const { t } = useTranslation();
  const { isRtl } = useLocale();

  const readiness = getTodayMetricValue(today, ['readiness', 'ctl']);
  const fatigue = getTodayMetricValue(today, ['fatigue', 'atl']);
  const recovery = getTodayMetricValue(today, ['recovery', 'tsb']);
  const status = getReadinessStatus(readiness);

  const clampedScore = Math.max(0, Math.min(100, readiness));
  /** RTL: low score at inline-start (right), high at inline-end (left) — mirror LTR thumb travel. */
  const thumbPercent = isRtl ? 100 - clampedScore : clampedScore;
  const thumbStartPercent = isRtl ? 100 : 0;

  const { ref: revealRef, revealed } = useRevealOnScroll<HTMLDivElement>({
    threshold: inline ? 0.2 : 0.35,
  });

  const animateOpts = { enabled: revealed, delayMs: inline ? 40 : 80 };

  const animatedThumbPercent = useAnimatedMetric(thumbPercent, {
    start: thumbStartPercent,
    ...animateOpts,
  });
  const animatedReadiness = useAnimatedMetric(readiness, animateOpts);
  const animatedFatigue = useAnimatedMetric(fatigue, animateOpts);
  const animatedRecovery = useAnimatedMetric(recovery, animateOpts);

  const displayReadiness = Math.round(animatedReadiness);
  const displayFatigue = Math.round(animatedFatigue);
  const displayRecovery = Math.round(animatedRecovery);

  const chipClass = status.chipClass ? ` ${status.chipClass}` : '';
  const spectrumClass = [
    'rd-spectrum',
    compact ? 'rd-spectrum--compact' : '',
    inline ? 'rd-spectrum--inline' : '',
    revealed ? 'rd-spectrum--revealed' : 'rd-spectrum--pending',
  ]
    .filter(Boolean)
    .join(' ');

  const tickKeys = [
    'calendar.readiness.status.rest',
    'calendar.readiness.status.coast',
    'calendar.readiness.status.build',
    'calendar.readiness.status.prime',
  ] as const;

  return (
    <div ref={revealRef} className="rd calendar-readiness" dir={isRtl ? 'rtl' : 'ltr'}>
      {inline && (
        <div className="rd-spectrum__section-label">{t('calendar.readiness.title')}</div>
      )}
      <div className={spectrumClass}>
        <div className="rd-spectrum__head rd-spectrum__head--chip-only">
          <span className={`rd__statuschip${chipClass}`}>
            {t(`calendar.readiness.statusSentence.${status.tone}`, { defaultValue: t(status.labelKey) })}
          </span>
        </div>

        <div className="rd-spectrum__bar-wrap">
          <div className="rd-spectrum__bar">
            <div
              className="rd-spectrum__thumb"
              style={{ ['--thumb-pos' as string]: `${animatedThumbPercent}%` }}
            >
              {displayReadiness}
            </div>
          </div>
          {!inline && (
            <div className="rd-spectrum__ticks" aria-hidden>
              {tickKeys.map((key) => (
                <span key={key}>{t(key)}</span>
              ))}
            </div>
          )}
        </div>

        {!inline && (
          <div className="rd-spectrum__legend">
            <div className="rd-spectrum__leg" data-kind="fatigue">
              <div className="rd-spectrum__leg-icon">
                <FatigueIcon />
              </div>
              <div className="rd-spectrum__leg-text">
                <span className="rd-spectrum__leg-label">{t('calendar.readiness.fatigue')}</span>
                <span className="rd-spectrum__leg-value">{displayFatigue}</span>
              </div>
            </div>
            <div className="rd-spectrum__leg" data-kind="readiness">
              <div className="rd-spectrum__leg-icon">
                <ReadinessIcon />
              </div>
              <div className="rd-spectrum__leg-text">
                <span className="rd-spectrum__leg-label">{t('readiness.scoreLabel')}</span>
                <span className="rd-spectrum__leg-value">{displayReadiness}</span>
              </div>
            </div>
            <div className="rd-spectrum__leg" data-kind="recovery">
              <div className="rd-spectrum__leg-icon">
                <RecoveryIcon />
              </div>
              <div className="rd-spectrum__leg-text">
                <span className="rd-spectrum__leg-label">{t('calendar.readiness.recovery')}</span>
                <span className="rd-spectrum__leg-value">{displayRecovery}</span>
              </div>
            </div>
          </div>
        )}

        {inline && (
          <div className="rd-spectrum__inline-stats" aria-label={t('calendar.readiness.title')}>
            <span data-kind="fatigue">
              <b>{t('calendar.readiness.fatigue')}</b>
              <em>{displayFatigue}</em>
            </span>
            <span data-kind="readiness">
              <b>{t('readiness.scoreLabel')}</b>
              <em>{displayReadiness}</em>
            </span>
            <span data-kind="recovery">
              <b>{t('calendar.readiness.recovery')}</b>
              <em>{displayRecovery}</em>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
